'use client';

import { Button, SkeletonBar, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';

import { validateStockStatus } from '../../../utils/stockStatusUtils';
import { MinimumQuantityText } from './minimum-quantity-text';
import './sdd-details.scss';

export type SddDetailsProps = Readonly<{
    isLoading: boolean;
    streetAddress?: string;
    availableStock: number;
    minimumCaseQty: number;
    isDoorDashAvailable: boolean;
    onOpenAddressDrawer?: () => void;
    className?: string;
    emphasizeAddress?: boolean;
}>;

export const SddDetails = ({
    isLoading,
    streetAddress,
    availableStock,
    minimumCaseQty,
    isDoorDashAvailable,
    onOpenAddressDrawer,
    className,
    emphasizeAddress = false,
}: SddDetailsProps) => {
    return (
        <div data-testid="sdd-delivery-details" className={cn('sdd-details', className)}>
            <SddAddressStatus
                availableStock={availableStock}
                minimumCaseQty={minimumCaseQty}
                isLoading={isLoading}
                address={streetAddress}
                isAvailable={isDoorDashAvailable}
                emphasizeAddress={emphasizeAddress}
            />
            <div className="delivery-details__change-store">
                {isLoading && (
                    <SkeletonBar
                        data-testid="change-store-skeleton"
                        className="delivery-details__change-store-skeleton"
                    />
                )}
                {!isLoading && (
                    <Button
                        type="button"
                        variant="link"
                        className="delivery-details__change-store-btn"
                        text={streetAddress ? 'Change' : 'Add address'}
                        data-testid="sdd-address-drawer-trigger"
                        onClick={onOpenAddressDrawer}
                    />
                )}
            </div>
            <SddDeliveryExtras
                isLoading={isLoading}
                minimumCaseQty={minimumCaseQty}
                isDelivery={availableStock > 0 && isDoorDashAvailable}
            />
        </div>
    );
};

type SddAddressStatusProps = Readonly<{
    isLoading: boolean;
    availableStock: number;
    minimumCaseQty: number;
    address?: string;
    isAvailable?: boolean;
    emphasizeAddress: boolean;
}>;

function SddAddressStatus({
    isLoading,
    address,
    availableStock,
    minimumCaseQty,
    isAvailable = true,
    emphasizeAddress,
}: SddAddressStatusProps) {
    if (isLoading) {
        return <SkeletonBar className="delivery-details__status-skeleton" data-testid="sdd-status-loading" />;
    }

    if (!address) {
        return (
            <TextPassage size="lg" data-testid="sdd-status-no-address">
                Add an address to check availability
            </TextPassage>
        );
    }

    const stockStatus = validateStockStatus({
        stockQuantity: availableStock,
        minimumCaseQty,
    });

    let stockText = 'Out of stock';
    if (stockStatus === 'InStock') stockText = 'Delivering';
    if (stockStatus === 'FewLeftInStock') stockText = 'Only a few left';
    if (!isAvailable) stockText = 'Not available';

    return (
        <TextPassage
            size="lg"
            className={cn({
                'sparky-u-color-utility-success': stockStatus === 'InStock' && isAvailable,
                'sparky-u-color-utility-error': stockStatus !== 'InStock' || !isAvailable,
            })}
            data-testid="sdd-status-content"
        >
            {stockText}
            <span className="delivery-details__store"> to </span>
            <span
                className={cn('delivery-details__store', emphasizeAddress && 'sparky-u-theme-typography-body-lg-bold')}
            >{`${address}`}</span>
        </TextPassage>
    );
}

type SddDeliveryExtrasProps = Readonly<{
    isLoading: boolean;
    minimumCaseQty: number;
    isDelivery: boolean;
}>;

function SddDeliveryExtras({ isLoading, minimumCaseQty, isDelivery }: SddDeliveryExtrasProps) {
    if (isLoading) {
        return <SkeletonBar className="delivery-details__curbside-skeleton" />;
    }

    return (
        <>
            {isDelivery && (
                <TextPassage data-testid="delivery-window-text" className="delivery-details__delivery-window-text">
                    Select a delivery window at checkout
                </TextPassage>
            )}
            <MinimumQuantityText minimumQuantity={minimumCaseQty} />
        </>
    );
}
